import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { itineraryAPI } from '../services/api';
import './ItineraryHistory.css'; // Shared styles

const UserItineraries = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  const fetchItineraries = useCallback(async () => {
    try {
      setLoading(true);
      const data = await itineraryAPI.getByUser(userId, {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      });
      setItineraries(data.data || data.itineraries || []);
      setTotalItems(data.pagination?.total || data.total || 0);
    } catch {
      setError('Failed to load user itineraries.');
    } finally {
      setLoading(false);
    }
  }, [userId, currentPage]);

  useEffect(() => { fetchItineraries(); }, [fetchItineraries]);

  const handleArchive = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Archive this itinerary?')) return;
    try {
      await itineraryAPI.archive(id);
      setSuccess('Itinerary archived.');
      setTimeout(() => setSuccess(''), 3000);
      fetchItineraries();
    } catch {
      setError('Failed to archive.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <Layout title={`User Itineraries — ${userId}`}>
      <div className="itin-history">
        <button className="itin-back-btn" onClick={() => navigate(-1)}>← Back</button>

        <div className="itin-page-header">
          <h2>Itineraries for User</h2>
          <span className="itin-total-badge" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {userId}
          </span>
        </div>

        {error   && <div className="itin-alert error">{error}</div>}
        {success && <div className="itin-alert success">{success}</div>}

        <div className="itin-table-wrap">
          <table className="itin-table">
            <thead>
              <tr>
                <th></th>
                <th>ID</th>
                <th>Type</th>
                <th>Interests</th>
                <th>Dates</th>
                <th>Guests</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="itin-td-center">Loading...</td></tr>
              ) : itineraries.length === 0 ? (
                <tr><td colSpan={9} className="itin-td-center">No itineraries for this user.</td></tr>
              ) : itineraries.map(item => (
                <React.Fragment key={item.id}>
                  <tr className={`itin-row ${expandedRows.has(item.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRow(item.id)}>
                    <td className="itin-expand-toggle">{expandedRows.has(item.id) ? '▼' : '▶'}</td>
                    <td className="itin-id">{String(item.id).slice(0, 8)}…</td>
                    <td>
                      <span className={`itin-type-badge ${item.plan_type === 'ai_generated' ? 'ai' : 'preset'}`}>
                        {item.plan_type === 'ai_generated' ? '🤖 AI' : '🗂️ Preset'}
                      </span>
                    </td>
                    <td className="itin-interests">
                      {(Array.isArray(item.interests) ? item.interests : [item.interests]).filter(Boolean).map(i => (
                        <span key={i} className="itin-chip">{i}</span>
                      ))}
                    </td>
                    <td>{item.from_date ? `${item.from_date} → ${item.to_date}` : '—'}</td>
                    <td>{item.guests ?? '—'}</td>
                    <td>
                      {item.is_archived
                        ? <span className="itin-status archived">Archived</span>
                        : <span className="itin-status active">Active</span>}
                    </td>
                    <td>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <div className="itin-action-btns">
                        <button className="itin-btn-icon view" title="View detail"
                          onClick={e => { e.stopPropagation(); navigate(`/itinerary/history/${item.id}`); }}>
                          👁️
                        </button>
                        {!item.is_archived && (
                          <button className="itin-btn-icon archive" title="Archive"
                            onClick={e => handleArchive(item.id, e)}>📦</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedRows.has(item.id) && (
                    <tr className="itin-expanded-row">
                      <td colSpan={9}>
                        <div className="itin-expanded-content">
                          <pre className="itin-json-preview">{JSON.stringify(item.plan || item, null, 2)}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="itin-pagination">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>◀ Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ▶</button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserItineraries;
